export const PROGRAMME_PORTAL_DATA = Object.freeze({
  zone: {
    defaultProfile: {
      fullName: 'Maya Clarke',
      email: 'maya@example.com',
      initials: 'MC',
      applicationReference: 'SNZ-APP-0142',
      applicationStatus: 'approved',
    },
    updates: [
      {
        id: 'zone-approved',
        date: '12 May 2026',
        title: 'Your application has been approved.',
        summary: 'Welcome to the Experimentation Zone.',
        body:
          'Your proposal has completed the initial review. The next step is to complete research onboarding so the programme team can confirm your responsibilities, safeguards and access.',
        actionLabel: 'Start onboarding',
        actionView: 'onboarding',
      },
      {
        id: 'zone-onboarding',
        date: '10 May 2026',
        title: 'Research onboarding is ready.',
        summary: 'Review the responsibilities and access checklist.',
        body:
          'Your onboarding checklist brings together the participant responsibilities, research contact details and safeguard confirmation required before your credential is activated.',
        actionLabel: 'Review checklist',
        actionView: 'onboarding',
      },
      {
        id: 'zone-review',
        date: '02 May 2026',
        title: 'Application review completed.',
        summary: 'The programme team has completed the first review.',
        body:
          'The programme team has reviewed the experiment scope, intended participants and stated safeguards. Your application record remains available in the portal.',
        actionLabel: 'View application',
        actionView: 'application',
      },
    ],
    resources: [
      {
        id: 'zone-onboarding-guide',
        title: 'Research onboarding guide',
        type: 'Guide',
        description:
          'A practical overview of the Zone onboarding process, research responsibilities and access requirements.',
        sections: [
          'Research responsibilities and programme contacts',
          'Evidence, reporting and decision records',
          'Safeguard and participant requirements',
        ],
      },
      {
        id: 'zone-participant-responsibilities',
        title: 'Participant responsibilities',
        type: 'Document',
        description:
          'The responsibilities every approved team must understand before research access is activated.',
        sections: [
          'Act within the approved experiment scope',
          'Report material changes and incidents promptly',
          'Maintain appropriate privacy, safety and oversight controls',
        ],
      },
      {
        id: 'zone-scoping-template',
        title: 'Experiment scoping template',
        type: 'Template',
        description:
          'A structured template for documenting the question, intervention, participants, safeguards and intended evidence.',
        sections: [
          'Experiment question and intended outcome',
          'Participants, systems and dependencies',
          'Evidence plan, safeguards and stop conditions',
        ],
      },
    ],
    onboardingTasks: [
      {
        id: 'research-contact',
        title: 'Confirm your research contact',
        description:
          'Confirm the person who will coordinate programme questions and experiment updates.',
        complete: true,
        fields: [
          {
            name: 'contactName',
            label: 'Research contact name',
            type: 'text',
            placeholder: 'Full name',
            defaultValue: 'Maya Clarke',
            required: true,
          },
          {
            name: 'contactEmail',
            label: 'Research contact email',
            type: 'email',
            placeholder: 'name@organisation.com',
            defaultValue: 'maya@example.com',
            required: true,
          },
        ],
      },
      {
        id: 'responsibilities',
        title: 'Review participant responsibilities',
        description:
          'Read the participant responsibilities and confirm that the team understands them.',
        complete: true,
        fields: [
          {
            name: 'responsibilitiesAccepted',
            label:
              'I have reviewed the participant responsibilities with the team.',
            type: 'checkbox',
            defaultValue: true,
            required: true,
          },
        ],
      },
      {
        id: 'safeguards',
        title: 'Confirm safeguards and approvals',
        description:
          'Record the safeguards, permissions or review steps already in place for the proposed experiment.',
        complete: false,
        fields: [
          {
            name: 'safeguardSummary',
            label: 'Safeguard and approval summary',
            type: 'textarea',
            placeholder:
              'Describe the controls, approvals and accountable owners.',
            defaultValue: '',
            required: true,
          },
        ],
      },
      {
        id: 'credential-activation',
        title: 'Request credential activation',
        description:
          'Confirm the final details that the programme team will use to activate the research credential.',
        complete: false,
        fields: [
          {
            name: 'credentialName',
            label: 'Name to show on the credential',
            type: 'text',
            placeholder: 'Credential holder name',
            defaultValue: 'Maya Clarke',
            required: true,
          },
          {
            name: 'credentialConfirmation',
            label:
              'I confirm that the information supplied for this credential is accurate.',
            type: 'checkbox',
            defaultValue: false,
            required: true,
          },
        ],
      },
    ],
  },
  fellowship: {
    defaultProfile: {
      fullName: 'Maya Clarke',
      email: 'maya@example.com',
      initials: 'MC',
      applicationReference: 'SNF-APP-0286',
      applicationStatus: 'selected',
    },
    updates: [
      {
        id: 'fellowship-welcome',
        date: '2 days ago',
        title: 'Welcome to the fellowship.',
        summary: 'Read a note from the programme team.',
        body:
          'We are delighted to welcome you to the fellowship. Your portal is the home for programme updates, onboarding, cohort connections and shared resources.',
        actionLabel: 'Start onboarding',
        actionView: 'onboarding',
      },
      {
        id: 'fellowship-onboarding',
        date: '3 days ago',
        title: 'Onboarding is now open.',
        summary: 'Start your programme setup tasks.',
        body:
          'Complete your profile, share your availability and write a short introduction before the cohort welcome session.',
        actionLabel: 'Review checklist',
        actionView: 'onboarding',
      },
      {
        id: 'fellowship-welcome-session',
        date: '5 days ago',
        title: 'Cohort welcome session',
        summary: 'Thursday, 21 May at 10:00 AM AST.',
        body:
          'The first cohort session will introduce the programme rhythm, working agreements and the people building alongside you.',
        actionLabel: 'View cohort',
        actionView: 'cohort',
      },
    ],
    resources: [
      {
        id: 'fellowship-welcome-guide',
        title: 'Welcome guide',
        type: 'Guide',
        description:
          'A concise introduction to the fellowship, programme rhythm and first-week actions.',
        sections: [
          'How the fellowship works',
          'First-week checklist',
          'Where to find help',
        ],
      },
      {
        id: 'fellowship-handbook',
        title: 'Programme handbook',
        type: 'Document',
        description:
          'The shared programme handbook covering expectations, sessions, outputs and support.',
        sections: [
          'Programme calendar and milestones',
          'Participation expectations',
          'Support, feedback and shared outputs',
        ],
      },
      {
        id: 'fellowship-working-together',
        title: 'Working together',
        type: 'Resource',
        description:
          'The cohort working agreements for thoughtful feedback, confidentiality and collaboration.',
        sections: [
          'Giving and receiving useful feedback',
          'Confidentiality and attribution',
          'Inclusive participation',
        ],
      },
    ],
    onboardingTasks: [
      {
        id: 'offer',
        title: 'Accept your offer',
        description:
          'Review the fellowship offer and confirm your place in the cohort.',
        complete: true,
        fields: [
          {
            name: 'offerAccepted',
            label: 'I accept my place in the fellowship.',
            type: 'checkbox',
            defaultValue: true,
            required: true,
          },
        ],
      },
      {
        id: 'profile',
        title: 'Complete your profile',
        description:
          'Add the short profile that will help other fellows understand your work.',
        complete: true,
        fields: [
          {
            name: 'preferredName',
            label: 'Preferred name',
            type: 'text',
            placeholder: 'How should the cohort address you?',
            defaultValue: 'Maya',
            required: true,
          },
          {
            name: 'shortBio',
            label: 'Short biography',
            type: 'textarea',
            placeholder: 'Share a short introduction to your work.',
            defaultValue:
              'Researcher and practitioner exploring responsible public-interest technology.',
            required: true,
          },
        ],
      },
      {
        id: 'availability',
        title: 'Share your availability',
        description:
          'Select the session window that works best for your usual schedule.',
        complete: false,
        fields: [
          {
            name: 'sessionWindow',
            label: 'Preferred session window',
            type: 'select',
            options: [
              'Weekday mornings',
              'Weekday afternoons',
              'Weekday evenings',
              'Flexible',
            ],
            defaultValue: '',
            required: true,
          },
          {
            name: 'timeZone',
            label: 'Time zone',
            type: 'text',
            placeholder: 'For example, AST (UTC−4)',
            defaultValue: 'AST (UTC−4)',
            required: true,
          },
        ],
      },
      {
        id: 'introduction',
        title: 'Write an introduction',
        description:
          'Share a short note about what you are exploring and what you hope to learn with the cohort.',
        complete: false,
        fields: [
          {
            name: 'introduction',
            label: 'Cohort introduction',
            type: 'textarea',
            placeholder:
              'Introduce your work, current question and what you hope to exchange with the cohort.',
            defaultValue: '',
            required: true,
          },
        ],
      },
    ],
    cohort: [
      {
        id: 'al',
        initials: 'AL',
        name: 'Amara Lewis',
        role: 'Civic design practitioner',
        focus: 'Designing more accessible public services.',
      },
      {
        id: 'jr',
        initials: 'JR',
        name: 'Jonah Reid',
        role: 'Data and policy researcher',
        focus: 'Public-interest data systems and evidence.',
      },
      {
        id: 'ns',
        initials: 'NS',
        name: 'Nia Singh',
        role: 'Community technologist',
        focus: 'Technology shaped with and by communities.',
      },
      {
        id: 'tk',
        initials: 'TK',
        name: 'Theo King',
        role: 'Independent product builder',
        focus: 'Responsible tools for small organisations.',
      },
      {
        id: 'rm',
        initials: 'RM',
        name: 'Rhea Moore',
        role: 'Climate innovation lead',
        focus: 'Adaptation tools grounded in local knowledge.',
      },
    ],
  },
});

export const getProgrammePortalData = (kind) =>
  PROGRAMME_PORTAL_DATA[kind] || PROGRAMME_PORTAL_DATA.zone;
